import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Loader2 } from 'lucide-react';
import PhoneInput from './PhoneInput';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TITLE_OPTIONS = [
  { value: '', label: 'Select your title' },
  { value: 'GM', label: 'General Manager' },
  { value: 'Marketing Director', label: 'Marketing Director' },
  { value: 'Owner', label: 'Owner' },
  { value: 'Sales Manager', label: 'Sales Manager' },
  { value: 'Other', label: 'Other' },
];

const DMS_OPTIONS = [
  { value: '', label: 'Select your DMS' },
  { value: 'CDK Global', label: 'CDK Global' },
  { value: 'Reynolds & Reynolds', label: 'Reynolds & Reynolds' },
  { value: 'Dealertrack', label: 'Dealertrack' },
  { value: 'Tekion', label: 'Tekion' },
  { value: 'PBS Systems', label: 'PBS' },
  { value: 'AutoMate', label: 'Auto/Mate' },
  { value: 'DealerBuilt', label: 'DealerBuilt' },
  { value: 'VUE', label: 'VUE' },
  { value: 'Other', label: 'Other' },
];

const IMS_OPTIONS = [
  { value: '', label: 'Select your IMS' },
  { value: 'vAuto', label: 'vAuto' },
  { value: 'FirstLook (CDK)', label: 'FirstLook (CDK)' },
  { value: 'Homenet (Cox)', label: 'Homenet (Cox)' },
  { value: 'DealerSocket Inventory', label: 'DealerSocket Inventory' },
  { value: 'AutoIMS', label: 'AutoIMS' },
  { value: 'Lotpop', label: 'Lotpop' },
  { value: 'Darwin Automotive', label: 'Darwin Automotive' },
  { value: 'Other', label: 'Other' },
];

const AI_CAPABILITIES = [
  { id: 'custom_agents', label: 'Custom AI Agents', desc: 'Automate specific business processes' },
  { id: 'workflow_automation', label: 'Workflow Automation', desc: 'AI-powered process automation' },
  { id: 'custom_chatbot', label: 'Custom Chatbot / LLM', desc: 'Built for your business knowledge' },
  { id: 'ai_analytics', label: 'AI Analytics & Reporting', desc: 'Insights from your data' },
  { id: 'document_processing', label: 'Document & Data Processing', desc: 'Extract and process unstructured data' },
  { id: 'not_at_this_time', label: 'Not at this time', desc: "I'll explore this later" },
  { id: 'other_ai', label: 'Other', desc: 'Tell us what you have in mind' },
];

function Checkbox({ checked, onChange, label, desc }) {
  return (
    <button
      type="button"
      onClick={onChange}
      className="flex items-start gap-2.5 p-3 rounded-xl text-left w-full transition-all duration-150 border"
      style={{
        borderColor: checked ? 'rgba(201,168,76,0.5)' : 'rgba(255,255,255,0.08)',
        background: checked ? 'rgba(201,168,76,0.07)' : 'rgba(255,255,255,0.02)',
      }}
    >
      <div
        className="mt-0.5 w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-all"
        style={{
          borderColor: checked ? '#C9A84C' : 'rgba(255,255,255,0.25)',
          background: checked ? 'rgba(201,168,76,0.15)' : 'transparent',
        }}
      >
        {checked && (
          <svg width="8" height="7" viewBox="0 0 8 7" fill="none">
            <path d="M1 3.5l2 2 4-4" stroke="#C9A84C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </div>
      <div>
        <div className="text-xs font-semibold" style={{ color: checked ? '#fff' : 'rgba(255,255,255,0.65)' }}>{label}</div>
        {desc && <div className="text-[10px] mt-0.5" style={{ color: 'rgba(255,255,255,0.35)' }}>{desc}</div>}
      </div>
    </button>
  );
}

export default function WaitlistForm({ variant = 'hero' }) {
  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', title: '',
    dealershipName: '', rooftops: '',
    dms: '', dmsOther: '', ims: '', imsOther: '', titleOther: '', cellPhone: '',
  });
  const [aiCapabilities, setAiCapabilities] = useState([]);
  const [errors, setErrors] = useState({});
  const [phoneValid, setPhoneValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const toggleCap = (id) => {
    setAiCapabilities(prev => prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]);
    if (errors.aiCapabilities) setErrors(p => ({ ...p, aiCapabilities: '' }));
  };

  const validate = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = 'Required';
    if (!form.lastName.trim()) e.lastName = 'Required';
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Valid email required';
    if (!form.title) e.title = 'Required';
    if (!form.dealershipName.trim()) e.dealershipName = 'Required';
    if (!form.rooftops || Number(form.rooftops) < 1) e.rooftops = 'Enter at least 1';
    if (!form.dms) e.dms = 'Required';
    if (!form.ims) e.ims = 'Required';
    if (!form.cellPhone.trim()) e.cellPhone = 'Required';
    else if (!phoneValid) e.cellPhone = 'Please enter a valid phone number';
    if (aiCapabilities.length === 0) e.aiCapabilities = 'Select at least one';
    return e;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, rooftops: parseInt(form.rooftops, 10), aiCapabilities }),
      });
      const data = await res.json();
      if (res.ok) {
        setSuccess(true);
        toast.success("You're on the list!", { description: "We'll be in touch with early access details." });
      } else {
        const detail = data.detail;
        const msg = Array.isArray(detail) ? detail.map(d => d.msg).join(', ') : String(detail || 'Something went wrong');
        toast.error('Submission failed', { description: msg });
      }
    } catch { toast.error('Network error', { description: 'Please try again.' }); }
    finally { setLoading(false); }
  };

  const inputCls = 'w-full px-4 py-3 rounded-xl text-white text-sm placeholder:text-white/35 border border-white/15 bg-black/50 input-gold outline-none transition-colors duration-200';
  const labelCls = 'block text-xs font-medium text-white/70 mb-1.5';
  const errCls = 'text-xs mt-1 text-red-400';

  if (success) {
    return (
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
        className="rounded-2xl border border-[#C9A84C]/30 bg-white/[0.04] p-8 text-center"
        style={{ backdropFilter: 'blur(12px)' }}>
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: 'rgba(201,168,76,0.15)' }}>
            <CheckCircle size={32} style={{ color: '#C9A84C' }} />
          </div>
        </div>
        <h3 className="text-xl font-semibold text-white mb-2" style={{ fontFamily: 'Space Grotesk' }}>You&apos;re on the list!</h3>
        <p className="text-white/60 text-sm leading-relaxed">We&apos;ll reach out with early access details. Dealer-only. No spam.</p>
        <div className="mt-5 text-xs text-white/40 font-mono-accent">SALIBALABS.AI — EARLY ACCESS</div>
      </motion.div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate
      className="rounded-2xl border border-white/10 bg-white/[0.04] p-6 sm:p-7"
      style={{ backdropFilter: 'blur(16px)', boxShadow: '0 0 0 1px rgba(201,168,76,0.08), 0 24px 80px rgba(0,0,0,0.55)' }}>

      {variant === 'hero' && (
        <div className="mb-5">
          <div className="font-mono-accent text-xs text-[#C9A84C] tracking-widest uppercase mb-1">Saliba&rsquo;s Latest Move</div>
          <h3 className="text-xl font-semibold text-white" style={{ fontFamily: 'Space Grotesk' }}>Get Early Access</h3>
          <p className="text-xs text-white/50 mt-1">A select group of dealerships is getting in first.</p>
        </div>
      )}

      <div className="space-y-3.5">
        {/* First + Last */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="firstName" className={labelCls}>First Name <span style={{color:'#C9A84C'}}>*</span></label>
            <input id="firstName" name="firstName" value={form.firstName} onChange={handleChange}
              placeholder="George" className={inputCls} data-testid="waitlist-first-name-input" autoComplete="given-name" />
            {errors.firstName && <p className={errCls}>{errors.firstName}</p>}
          </div>
          <div>
            <label htmlFor="lastName" className={labelCls}>Last Name <span style={{color:'#C9A84C'}}>*</span></label>
            <input id="lastName" name="lastName" value={form.lastName} onChange={handleChange}
              placeholder="Saliba" className={inputCls} data-testid="waitlist-last-name-input" autoComplete="family-name" />
            {errors.lastName && <p className={errCls}>{errors.lastName}</p>}
          </div>
        </div>

        {/* Email */}
        <div>
          <label htmlFor="email" className={labelCls}>Work Email <span style={{color:'#C9A84C'}}>*</span></label>
          <input id="email" name="email" type="email" value={form.email} onChange={handleChange}
            placeholder="george@dealership.com" className={inputCls} data-testid="waitlist-email-input"
            autoComplete="email" />
          {errors.email && <p className={errCls}>{errors.email}</p>}
        </div>

        {/* Title */}
        <div>
          <label htmlFor="title" className={labelCls}>Your Title <span style={{color:'#C9A84C'}}>*</span></label>
          <select id="title" name="title" value={form.title} onChange={handleChange}
            className={inputCls} data-testid="waitlist-title-select" style={{ cursor: 'pointer' }}>
            {TITLE_OPTIONS.map(o => <option key={o.value} value={o.value} disabled={!o.value}>{o.label}</option>)}
          </select>
          {errors.title && <p className={errCls}>{errors.title}</p>}
          {form.title === 'Other' && (
            <input name="titleOther" value={form.titleOther} onChange={handleChange}
              placeholder="What is your title?" className={`${inputCls} mt-2`} autoFocus />
          )}
        </div>

        {/* Dealership + Rooftops */}
        <div>
          <label htmlFor="dealershipName" className={labelCls}>Dealership Name <span style={{color:'#C9A84C'}}>*</span></label>
          <input id="dealershipName" name="dealershipName" value={form.dealershipName} onChange={handleChange}
            placeholder="AutoHaus Group" className={inputCls} data-testid="waitlist-dealership-name-input" />
          {errors.dealershipName && <p className={errCls}>{errors.dealershipName}</p>}
        </div>

        <div>
          <label htmlFor="rooftops" className={labelCls}>Number of Rooftops <span style={{color:'#C9A84C'}}>*</span></label>
          <input id="rooftops" name="rooftops" type="number" min="1" value={form.rooftops} onChange={handleChange}
            placeholder="How many locations?" className={inputCls} data-testid="waitlist-rooftops-input" />
          {errors.rooftops && <p className={errCls}>{errors.rooftops}</p>}
        </div>

        {/* DMS + IMS */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="dms" className={labelCls}>Dealer Mgmt System <span style={{color:'#C9A84C'}}>*</span></label>
            <select id="dms" name="dms" value={form.dms} onChange={handleChange}
              className={inputCls} data-testid="waitlist-dms-select" style={{ cursor: 'pointer' }}>
              {DMS_OPTIONS.map(o => <option key={o.value} value={o.value} disabled={!o.value}>{o.label}</option>)}
            </select>
            {errors.dms && <p className={errCls}>{errors.dms}</p>}
            {form.dms === 'Other' && (
              <input name="dmsOther" value={form.dmsOther} onChange={handleChange}
                placeholder="Which DMS do you use?" className={`${inputCls} mt-2`} autoFocus />
            )}
          </div>
          <div>
            <label htmlFor="ims" className={labelCls}>Inventory Mgmt System <span style={{color:'#C9A84C'}}>*</span></label>
            <select id="ims" name="ims" value={form.ims} onChange={handleChange}
              className={inputCls} data-testid="waitlist-ims-select" style={{ cursor: 'pointer' }}>
              {IMS_OPTIONS.map(o => <option key={o.value} value={o.value} disabled={!o.value}>{o.label}</option>)}
            </select>
            {errors.ims && <p className={errCls}>{errors.ims}</p>}
            {form.ims === 'Other' && (
              <input name="imsOther" value={form.imsOther} onChange={handleChange}
                placeholder="Which IMS do you use?" className={`${inputCls} mt-2`} />
            )}
          </div>
        </div>

        {/* Cell Phone */}
        <PhoneInput
          value={form.cellPhone}
          onChange={(full, digits, country) => {
            setForm(p => ({ ...p, cellPhone: full }));
            setPhoneValid(digits.length >= country.minDigits && digits.length <= country.maxDigits);
            if (errors.cellPhone) setErrors(p => ({ ...p, cellPhone: '' }));
          }}
          error={errors.cellPhone}
          required
          testId="waitlist-cell-phone-input"
        />

        {/* AI Capabilities */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className={labelCls + ' mb-0'}>Additional AI capabilities? <span style={{color:'#C9A84C'}}>*</span></label>
            <span className="text-[10px] text-white/35 font-mono-accent">Select all that apply</span>
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            {AI_CAPABILITIES.map(cap => (
              <Checkbox
                key={cap.id}
                checked={aiCapabilities.includes(cap.id)}
                onChange={() => toggleCap(cap.id)}
                label={cap.label}
                desc={cap.desc}
              />
            ))}
          </div>
          {errors.aiCapabilities && <p className={errCls}>{errors.aiCapabilities}</p>}
        </div>
      </div>

      <button type="submit" disabled={loading}
        className="w-full mt-5 py-3.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all duration-200"
        style={{ background: loading ? 'rgba(201,168,76,0.5)' : '#C9A84C', color: '#0a0a0a', cursor: loading ? 'not-allowed' : 'pointer' }}
        data-testid={variant === 'final' ? 'final-waitlist-submit-button' : 'waitlist-submit-button'}>
        {loading ? <><Loader2 size={16} className="animate-spin" /> Submitting&hellip;</> : <>Request Early Access</>}
      </button>
      <p className="text-center text-xs text-white/35 mt-3">No spam. Dealer-only early access.</p>
    </form>
  );
}
